/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import './TemplateEditor.css';
import Placeholder from '@tiptap/extension-placeholder';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Highlight from '@tiptap/extension-highlight';
import TiptapTypography from '@tiptap/extension-typography';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import D3Chart from './extensions/D3ChartExtension';
import ChartInsertModal from './ChartInsertModal';
import ChartConfigModal from './ChartConfigModal';
import {
  Box,
  Paper,
  IconButton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  Tooltip,
  Select,
  FormControl,
  Chip,
  Typography
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Undo,
  Redo,
  FormatClear,
  TableChart,
  Image as ImageIcon,
  FormatColorText,
  FormatColorFill,
  ArrowDropDown,
  Functions,
  Lock as LockIcon,
  InsertChart as ChartIcon
} from '@mui/icons-material';

// MenuBar component for the editor toolbar
// eslint-disable-next-line max-lines-per-function
const MenuBar = ({ editor, onInsertVariable, disabled = false }) => {
  const [textColorAnchor, setTextColorAnchor] = useState(null);
  const [bgColorAnchor, setBgColorAnchor] = useState(null);
  const [tableAnchor, setTableAnchor] = useState(null);

  if (!editor) {
    return null;
  }

  const colors = [
    '#000000', '#434343', '#666666', '#999999', '#cccccc', '#ffffff',
    '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff',
    '#9900ff', '#ff00ff', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3',
    '#d0e0e3', '#cfe2f3', '#d9d2e9', '#ead1dc'
  ];

  const handleTextColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setTextColorAnchor(null);
  };

  const handleBgColor = (color) => {
    editor.chain().focus().toggleHighlight({ color }).run();
    setBgColorAnchor(null);
  };

  const addTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setTableAnchor(null);
  };

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/svg+xml';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        // Create a temporary URL for the image
        const tempUrl = URL.createObjectURL(file);

        // Insert image into editor
        editor.chain().focus().setImage({
          src: tempUrl,
          alt: file.name,
          title: file.name
        }).run();

        // TODO: Upload to entity's branding bucket for persistence
        // For now, images are temporary and will be lost on refresh
        console.log('Image inserted temporarily. Upload to GCS not yet implemented.');
      } catch (error) {
        console.error('Failed to add image:', error);
      }
    };
    input.click();
  };

  const addImageFromUrl = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      p: 1,
      borderBottom: 1,
      borderColor: 'divider',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto'
    }}>
      {/* Text style selector */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
          value={
            editor.isActive('heading', { level: 1 }) ? 'h1' :
            editor.isActive('heading', { level: 2 }) ? 'h2' :
            editor.isActive('heading', { level: 3 }) ? 'h3' :
            editor.isActive('heading', { level: 4 }) ? 'h4' :
            editor.isActive('heading', { level: 5 }) ? 'h5' :
            editor.isActive('heading', { level: 6 }) ? 'h6' :
            'paragraph'
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'paragraph') {
              editor.chain().focus().setParagraph().run();
            } else {
              const level = parseInt(value.replace('h', ''));
              editor.chain().focus().toggleHeading({ level }).run();
            }
          }}
        >
          <MenuItem value="paragraph">Normal</MenuItem>
          <MenuItem value="h1">Heading 1</MenuItem>
          <MenuItem value="h2">Heading 2</MenuItem>
          <MenuItem value="h3">Heading 3</MenuItem>
          <MenuItem value="h4">Heading 4</MenuItem>
          <MenuItem value="h5">Heading 5</MenuItem>
          <MenuItem value="h6">Heading 6</MenuItem>
        </Select>
      </FormControl>

      <Divider orientation="vertical" flexItem />

      {/* Text formatting */}
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlined />
        </ToggleButton>
        <ToggleButton
          value="strike"
          selected={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <FormatStrikethrough />
        </ToggleButton>
        <ToggleButton
          value="code"
          selected={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Colors */}
      <Tooltip title="Text Color">
        <IconButton size="small" onClick={(e) => setTextColorAnchor(e.currentTarget)}>
          <FormatColorText />
          <ArrowDropDown />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={textColorAnchor}
        open={Boolean(textColorAnchor)}
        onClose={() => setTextColorAnchor(null)}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, p: 1 }}>
          {colors.map(color => (
            <Box
              key={color}
              sx={{
                width: 24,
                height: 24,
                backgroundColor: color,
                border: '1px solid #ccc',
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => handleTextColor(color)}
            />
          ))}
        </Box>
      </Menu>

      <Tooltip title="Background Color">
        <IconButton size="small" onClick={(e) => setBgColorAnchor(e.currentTarget)}>
          <FormatColorFill />
          <ArrowDropDown />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={bgColorAnchor}
        open={Boolean(bgColorAnchor)}
        onClose={() => setBgColorAnchor(null)}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, p: 1 }}>
          {colors.map(color => (
            <Box
              key={color}
              sx={{
                width: 24,
                height: 24,
                backgroundColor: color,
                border: '1px solid #ccc',
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => handleBgColor(color)}
            />
          ))}
        </Box>
      </Menu>

      <Divider orientation="vertical" flexItem />

      {/* Lists */}
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulleted />
        </ToggleButton>
        <ToggleButton
          value="orderedList"
          selected={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumbered />
        </ToggleButton>
        <ToggleButton
          value="blockquote"
          selected={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <FormatQuote />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Alignment */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={
          editor.isActive({ textAlign: 'left' }) ? 'left' :
          editor.isActive({ textAlign: 'center' }) ? 'center' :
          editor.isActive({ textAlign: 'right' }) ? 'right' :
          editor.isActive({ textAlign: 'justify' }) ? 'justify' :
          'left'
        }
      >
        <ToggleButton
          value="left"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value="center"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value="right"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <FormatAlignRight />
        </ToggleButton>
        <ToggleButton
          value="justify"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        >
          <FormatAlignJustify />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Insert items */}
      <Tooltip title="Insert Table">
        <IconButton size="small" onClick={(e) => setTableAnchor(e.currentTarget)}>
          <TableChart />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={tableAnchor}
        open={Boolean(tableAnchor)}
        onClose={() => setTableAnchor(null)}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
            Select table size (rows × columns)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5 }}>
            {[...Array(5)].map((_, row) => (
              [...Array(5)].map((_, col) => (
                <Tooltip key={`${row}-${col}`} title={`${row + 1} × ${col + 1} table`} placement="top">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        '& ~ div': { backgroundColor: 'primary.lighter' }
                      },
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => addTable(row + 1, col + 1)}
                    onMouseEnter={(e) => {
                      // Highlight all cells up to this one
                      const grid = e.currentTarget.parentElement;
                      const cells = grid.querySelectorAll('div');
                      cells.forEach((cell, idx) => {
                        const cellRow = Math.floor(idx / 5);
                        const cellCol = idx % 5;
                        if (cellRow <= row && cellCol <= col) {
                          cell.style.backgroundColor = '#e3f2fd';
                        }
                      });
                    }}
                    onMouseLeave={(e) => {
                      // Reset all cells
                      const grid = e.currentTarget.parentElement;
                      const cells = grid.querySelectorAll('div');
                      cells.forEach(cell => {
                        cell.style.backgroundColor = '';
                      });
                    }}
                  />
                </Tooltip>
              ))
            ))}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary' }}>
            Click to insert table
          </Typography>
        </Box>
      </Menu>

      <Tooltip title="Insert Image">
        <IconButton size="small" onClick={addImage}>
          <ImageIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Insert Image from URL">
        <IconButton size="small" onClick={addImageFromUrl}>
          <ImageIcon color="primary" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Insert Variable">
        <IconButton size="small" onClick={onInsertVariable} color="primary">
          <Functions />
        </IconButton>
      </Tooltip>

      <Tooltip title="Insert Chart">
        <IconButton size="small" onClick={() => window.dispatchEvent(new Event('open-chart-modal'))} color="secondary">
          <ChartIcon />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* History */}
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
      >
        <FormatClear />
      </IconButton>
    </Box>
  );
};

// Custom extension to highlight variables
const VariableHighlight = {
  name: 'variableHighlight',
  addOptions() {
    return {
      variableClass: 'template-variable',
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          class: {
            default: null,
            parseHTML: element => {
              const text = element.textContent || '';
              return /\{\{[^}]+\}\}/.test(text) ? this.options.variableClass : null;
            },
            renderHTML: attributes => {
              if (!attributes.class) {
                return {};
              }
              return { class: attributes.class };
            },
          },
        },
      },
    ];
  },
};

// eslint-disable-next-line max-lines-per-function
const TemplateEditor = ({
  initialContent = '',
  onChange,
  variables = [],
  onVariableInsert,
  placeholder = 'Start typing your template...',
  disabled = false
}) => {
  const [variableMenuAnchor, setVariableMenuAnchor] = useState(null);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartConfigOpen, setChartConfigOpen] = useState(false);
  const [configChartData, setConfigChartData] = useState(null);
  const [_configNodePos, setConfigNodePos] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Color,
      TextStyle,
      Underline,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Highlight.configure({
        multicolor: true,
      }),
      TiptapTypography,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image,
      VariableHighlight,
      D3Chart,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) {
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
      handleDOMEvents: {
        contextmenu: (view, event) => {
          // Check if right-clicked on empty area
          const { state } = view;
          const { selection } = state;
          const { empty } = selection;

          if (empty) {
            event.preventDefault();

            // Show custom context menu
            const menu = document.createElement('div');
            menu.style.position = 'fixed';
            menu.style.left = `${event.clientX}px`;
            menu.style.top = `${event.clientY}px`;
            menu.style.backgroundColor = 'white';
            menu.style.border = '1px solid #ccc';
            menu.style.borderRadius = '4px';
            menu.style.padding = '8px';
            menu.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
            menu.style.zIndex = '9999';
            menu.style.cursor = 'pointer';

            const menuItem = document.createElement('div');
            menuItem.textContent = '📊 Add D3.js Chart';
            menuItem.style.padding = '8px 16px';
            menuItem.style.borderRadius = '4px';
            menuItem.onmouseover = () => {
              menuItem.style.backgroundColor = '#f0f0f0';
            };
            menuItem.onmouseout = () => {
              menuItem.style.backgroundColor = 'transparent';
            };
            menuItem.onclick = () => {
              setChartModalOpen(true);
              document.body.removeChild(menu);
            };

            menu.appendChild(menuItem);
            document.body.appendChild(menu);

            // Remove menu when clicking elsewhere
            const removeMenu = () => {
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
              document.removeEventListener('click', removeMenu);
            };
            setTimeout(() => {
              document.addEventListener('click', removeMenu);
            }, 100);

            return true;
          }
          return false;
        }
      }
    },
    editable: !disabled,
  });

  // Update editor content when initialContent changes
  useEffect(() => {
    if (editor && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent);
    }
  }, [editor, initialContent]);

  // Update editor editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  // Set up event listeners for chart configuration
  useEffect(() => {
    const handleOpenChartModal = () => {
      setChartModalOpen(true);
    };

    const handleConfigureChart = (event) => {
      const { nodePos, chartType, chartData, chartOptions, updateAttributes } = event.detail;
      setConfigNodePos(nodePos);
      setConfigChartData({
        chartType,
        chartData,
        chartOptions,
        updateAttributes
      });
      setChartConfigOpen(true);
    };

    window.addEventListener('open-chart-modal', handleOpenChartModal);
    window.addEventListener('configure-chart', handleConfigureChart);

    return () => {
      window.removeEventListener('open-chart-modal', handleOpenChartModal);
      window.removeEventListener('configure-chart', handleConfigureChart);
    };
  }, []);

  const insertVariable = (variable) => {
    if (editor) {
      editor.chain().focus().insertContent(`{{${variable.name}}}`).run();
    }
    setVariableMenuAnchor(null);
    if (onVariableInsert) {
      onVariableInsert(variable);
    }
  };

  const handleInsertVariable = (event) => {
    setVariableMenuAnchor(event.currentTarget);
  };

  const handleInsertChart = (chartConfig) => {
    if (editor) {
      editor.chain().focus().insertChart(chartConfig).run();
    }
  };

  const handleSaveChartConfig = (config) => {
    if (configChartData && configChartData.updateAttributes) {
      configChartData.updateAttributes(config);
    }
  };

  return (
    <Paper elevation={0} sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      opacity: disabled ? 0.7 : 1,
      position: 'relative'
    }}>
      {disabled && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Chip
            icon={<LockIcon />}
            label="Template is locked by another user"
            color="warning"
          />
        </Box>
      )}
      <MenuBar editor={editor} onInsertVariable={handleInsertVariable} disabled={disabled} />
      <Box sx={{
        p: 2,
        minHeight: 400,
        '& .tiptap-editor': {
          outline: 'none',
          '& p': {
            margin: 0,
            marginBottom: '1em',
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '1em',
            marginBottom: '0.5em',
          },
          '& .template-variable': {
            backgroundColor: 'primary.light',
            color: 'primary.main',
            padding: '2px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
          },
          '& .ProseMirror-placeholder': {
            color: 'text.secondary',
            opacity: 0.5,
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '1em',
          },
          '& td, & th': {
            border: '1px solid #ddd',
            padding: '8px',
          },
          '& th': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
          },
        }
      }}>
        <EditorContent editor={editor} />
      </Box>

      {/* Variable insertion menu */}
      <Menu
        anchorEl={variableMenuAnchor}
        open={Boolean(variableMenuAnchor)}
        onClose={() => setVariableMenuAnchor(null)}
      >
        <Box sx={{ p: 1 }}>
          <Box sx={{ px: 1, pb: 1 }}>
            <strong>Insert Variable</strong>
          </Box>
          <Divider />
          {variables.length === 0 ? (
            <MenuItem disabled>No variables defined</MenuItem>
          ) : (
            variables.map((variable) => (
              <MenuItem
                key={variable.name}
                onClick={() => insertVariable(variable)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={variable.type}
                    size="small"
                    color={
                      variable.type === 'entity_field' ? 'primary' :
                      variable.type === 'system' ? 'secondary' :
                      'default'
                    }
                  />
                  <Box>
                    <Box sx={{ fontFamily: 'monospace' }}>{`{{${variable.name}}}`}</Box>
                    {variable.description && (
                      <Box sx={{ fontSize: '0.875em', color: 'text.secondary' }}>
                        {variable.description}
                      </Box>
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>

      {/* Chart Insert Modal */}
      <ChartInsertModal
        open={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        onInsert={handleInsertChart}
      />

      {/* Chart Configuration Modal */}
      <ChartConfigModal
        open={chartConfigOpen}
        onClose={() => {
          setChartConfigOpen(false);
          setConfigChartData(null);
          setConfigNodePos(null);
        }}
        chartConfig={configChartData}
        onSave={handleSaveChartConfig}
      />
    </Paper>
  );
};

export default TemplateEditor;